<template>
  <section :class="{ error: partyErrors }">
    <div class="row">
      <label class="adults-count">
        <translate tag="em" :tt="{ adultsCount: adults }"
          >{adultsCount} adults (18+)</translate
        >
      </label>

      <number-stepper
        v-model="adults"
        data-test="search-wizard-adults-number"
        :min="0"
        :max="30"
        :default-value="adults"
        @input="submitGuests"
      />
    </div>

    <div class="row">
      <label
        ><translate :tt="{ childrenCount: children }"
          >{childrenCount} children</translate
        ></label
      >
      <number-stepper
        v-model="children"
        data-test="search-wizard-children-number"
        :min="0"
        :max="30"
        :default-value="children"
        @input="submitGuests"
      />
    </div>

    <div v-if="children" class="children">
      <div class="row">
        <translate tag="label" :class="{ error: childAgesErrors }">
          Age of children
        </translate>
        <div class="child-ages">
          <template v-for="childIndex in children" :key="childIndex">
            <div
              class="ui-select"
              :class="{
                error: childAgesErrors && !(childAges[childIndex - 1] >= 0),
              }"
            >
              <span class="caret"><span class="l" /> <span class="r" /></span>
              <select
                v-model="childAges[childIndex - 1]"
                data-test="search-wizard-child-ages"
                @change="submitGuests"
              >
                <option value="" selected disabled>---</option>
                <translate
                  v-for="childAge in minAdultAge"
                  :key="childAge"
                  tag="option"
                  :value="childAge"
                  :tt="{ childAge }"
                >
                  {childAge} years old
                </translate>
              </select>
            </div>
          </template>
        </div>
      </div>
      <transition name="fade" tag="div">
        <translate v-if="errors" tag="div" class="input-error-msg">
          {{ errors }}
        </translate>
      </transition>
    </div>
    <div class="row">
      <label class="dogs-count">
        <translate :tt="{ dogs }"> {dogs} dogs </translate>
      </label>

      <number-stepper
        v-model="dogs"
        data-test="search-wizard-dogs-number"
        :min="0"
        :max="30"
        :default-value="dogs"
        @input="submitGuests"
      />
    </div>
    <group-booking />
    <nuxt-to-meta :nuxt2meta="head" />
  </section>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import type { ValidationErrors } from '../../domain/ValidationErrors'
import type { SearchWizardParty } from './SearchWizardParty'

@Component({
  emits: ['changeGuests'],
})
class GuestsWizardBody extends Vue {
  @Prop({ required: true })
  guests: SearchWizardParty

  @Prop()
  validationErrors?: ValidationErrors

  adults = 2
  minAdultAge = [...Array(19).keys()]
  children = 0
  childAges: number[] = []
  dogs = 0

  created() {
    this.adults = this.guests.adults
    this.children = this.guests.childAges.length
    this.childAges = [...this.guests.childAges]
    this.dogs = this.guests.dogs
  }

  get head() {
    return {
      bodyAttrs: {
        class: this.$isDesktop ? '' : 'noscroll',
      },
    }
  }

  get sanitizedGuests(): SearchWizardParty {
    const childAges =
      this.childAges.length > this.children
        ? this.childAges.slice(0, this.children)
        : this.childAges
    return {
      adults: this.adults,
      childAges,
      children: this.children,
      dogs: this.dogs,
    }
  }

  get partyErrors() {
    return this.validationErrors?.party
  }

  get childAgesErrors() {
    return this.validationErrors?.childAges
  }

  get errors() {
    return this.partyErrors || this.childAgesErrors
  }

  submitGuests() {
    this.$emit('changeGuests', this.sanitizedGuests)
  }
}

export default toNative(GuestsWizardBody)
</script>
<style scoped>
section {
  margin: 1em 0.5em 1.5em;
}

label {
  font-size: inherit;
}

.adults-count {
  font-weight: bold;
}

.adults-count :deep(span) {
  color: var(--colour-text-light);
  font-weight: normal;
}

.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 1em 0;
  padding: 0 0 1em 0;
  border-bottom: 1px solid var(--colour-table-borders);
}

.children .row {
  align-items: flex-start;
  border-bottom: none;
  margin: 0 0;
  padding: 0 0;
}

.children {
  margin: 0 0 1em 0;
  padding: 0 0 1em 0;
  border-bottom: 1px solid var(--colour-table-borders);
}

.children label {
  margin-top: 0.35em;
}

.child-ages {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
}

.child-ages select {
  font-size: 1em;
}

.ui-select {
  flex-shrink: 0;
  margin: 0 0 0.75em 0;
}
</style>
